import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Mail, HelpCircle, Package, CreditCard, Shield, Users, MessageCircle, RefreshCw } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const faqs = [
  {
    category: 'Getting Started',
    icon: HelpCircle,
    color: 'from-blue-500 to-cyan-500',
    questions: [
      {
        q: 'What is Bid2Barter?',
        a: 'Bid2Barter is India\'s first barter-based marketplace where you can trade products you own for things you want. Unlike traditional platforms, you can offer items, cash, or a combination of both. Think of it as a modern auction combined with the age-old concept of barter trading!'
      },
      {
        q: 'How do I create an account?',
        a: 'Click "Login / Register" in the navigation bar, then select "Create Account". Fill in your name, email, location (city, state, country), and password. Once registered, you can immediately start listing items and placing bids.'
      },
      {
        q: 'Is Bid2Barter free to use?',
        a: 'Yes! Creating an account, browsing listings, creating listings, placing bids, and completing trades is completely free. There are no hidden fees or commissions on trades.'
      },
      {
        q: 'Can I browse without an account?',
        a: 'Absolutely! Guest browsing is fully supported. You can view all listings, see product details, and explore categories without registering. However, you\'ll need an account to place bids, create listings, or use the messaging feature.'
      },
      {
        q: 'What categories are available?',
        a: 'We offer 12 diverse categories: Electronics, Fashion, Furniture, Home, Vehicles, Books, Sports, Food, Pets & Plants, Coupons, Services, and Other. Each category has its own 3D visual identity to make browsing more engaging.'
      }
    ]
  },
  {
    category: 'Listings & Trading',
    icon: Package,
    color: 'from-emerald-500 to-teal-500',
    questions: [
      {
        q: 'How do I create a listing?',
        a: 'Click "Create" in the navigation menu. Add your product title, detailed description, select a category, upload photos/videos, set your location, choose your exchange preferences (items, cash, or both), and set a bidding deadline. Your listing goes live instantly!'
      },
      {
        q: 'How long does a listing stay active?',
        a: 'You choose the bidding deadline when creating your listing. The default is 7 days, but you can set any duration. As a seller, you can also extend the bidding period if you haven\'t received satisfactory offers yet.'
      },
      {
        q: 'Can I edit or delete my listing?',
        a: 'Yes! Go to "My Listings" to manage all your active listings. You can edit product details, update images, change preferences, or remove the listing entirely at any time before accepting a bid.'
      },
      {
        q: 'What makes a good listing?',
        a: 'A great listing includes: clear high-quality photos from multiple angles, detailed product description with condition information, fair exchange preferences, and accurate location. Listings with more details get significantly more bids.'
      },
      {
        q: 'How does the trade contract work?',
        a: 'When a bid is accepted, Bid2Barter automatically generates a Trade Contract between both parties. This contract outlines the exact items/cash being exchanged. Both parties can track the trade status and communicate through our built-in chat.'
      }
    ]
  },
  {
    category: 'Bidding System',
    icon: CreditCard,
    color: 'from-violet-500 to-purple-500',
    questions: [
      {
        q: 'How do I place a bid?',
        a: 'On any listing page, click "Place Bid". You can offer: (1) Items only from your inventory, (2) Cash only, or (3) A hybrid combination of items + cash. Add a message explaining your offer and submit.'
      },
      {
        q: 'Can a seller counter my bid?',
        a: 'Yes! Sellers can send counter-offers if they\'re interested but want to negotiate. You\'ll receive a notification and can accept the counter, decline it, or place a new improved bid.'
      },
      {
        q: 'How does the Compare Bids feature work?',
        a: 'Sellers with 2 or more bids can use the "Compare" button to view bids side-by-side. The comparison table shows cash offers, items offered, estimated total value, messages, and submission dates. "Best" and "Highest" badges help identify the strongest offers instantly.'
      },
      {
        q: 'Can I bid on multiple listings?',
        a: 'Yes, you can place bids on as many listings as you want. Manage all your active bids from the "My Bids" section. You can also edit or withdraw bids before they\'re accepted.'
      },
      {
        q: 'What happens after my bid is accepted?',
        a: 'A Trade Contract is created. You\'ll be notified immediately and can start chatting with the seller to arrange the exchange. Both parties confirm when the trade is complete, and can leave reviews for each other.'
      }
    ]
  },
  {
    category: 'Safety & Trust',
    icon: Shield,
    color: 'from-amber-500 to-orange-500',
    questions: [
      {
        q: 'How do I know if a seller is trustworthy?',
        a: 'Check the seller\'s profile for their star rating, review history, and number of completed trades. Users with more completed trades and positive reviews are generally more reliable. We also show how long they\'ve been a member.'
      },
      {
        q: 'What if I have a problem with a trade?',
        a: 'First, use our built-in messaging to communicate with the other party. If you can\'t resolve the issue, contact our support team at bid2barter@gmail.com with your trade details. We review all reported issues within 24-48 hours.'
      },
      {
        q: 'How do I report a suspicious listing?',
        a: 'Look for the "Report" option on any listing or user profile. Provide details about your concern, and our moderation team will investigate and take appropriate action.'
      },
      {
        q: 'Is my personal data secure?',
        a: 'Yes. Your contact information is only shared with users you\'re actively trading with. Passwords are encrypted using industry-standard bcrypt hashing. We never sell your data to third parties.'
      }
    ]
  },
  {
    category: 'Account & Profile',
    icon: Users,
    color: 'from-pink-500 to-rose-500',
    questions: [
      {
        q: 'How do I reset my password?',
        a: 'Click "Forgot Password" on the login page. Enter your registered email address, and you\'ll receive an OTP (One-Time Password) to verify your identity. Enter the OTP and set your new password. The OTP expires in 10 minutes.'
      },
      {
        q: 'What is the inventory feature?',
        a: 'Your inventory is your collection of items available for trade. Add items with photos, descriptions, and estimated values. When placing bids, you can quickly select items from your inventory instead of entering details each time.'
      },
      {
        q: 'How do reviews and ratings work?',
        a: 'After completing a trade, both parties can leave a review with 1-5 star rating and a written comment. Reviews are publicly visible on your profile and help build trust in the community. You cannot edit reviews once submitted.'
      },
      {
        q: 'Can I have multiple accounts?',
        a: 'No, each person should have only one Bid2Barter account. Multiple accounts for the same user may be flagged and suspended by our moderation team.'
      }
    ]
  },
  {
    category: 'Communication & Notifications',
    icon: MessageCircle,
    color: 'from-indigo-500 to-blue-500',
    questions: [
      {
        q: 'How does messaging work?',
        a: 'Once a bid is placed on a listing, a chat thread is created between the bidder and seller. Go to "Messages" in the navigation to view all your conversations. Messages are private and only visible to the two parties.'
      },
      {
        q: 'What notifications will I receive?',
        a: 'You\'ll be notified about: new bids on your listings, bid acceptance/rejection/counter-offers, new messages, trade status updates, and review notifications. Click the bell icon in the top navigation to view all notifications.'
      },
      {
        q: 'Will I receive email notifications?',
        a: 'Yes! We send email notifications for important events like bid acceptance, trade initiation, and new messages. You can manage your notification preferences from your profile settings.'
      },
      {
        q: 'How do I contact Bid2Barter support?',
        a: 'Email us at bid2barter@gmail.com with your query or issue. Include your account email and relevant details (trade ID, listing URL, etc.). Our support team typically responds within 24-48 hours.'
      }
    ]
  }
];

const FAQItem = ({ question, answer, isOpen, onClick }) => (
  <div className="border-b border-slate-200 last:border-b-0">
    <button
      onClick={onClick}
      className="w-full py-4 px-4 flex items-center justify-between text-left hover:bg-slate-50 transition-colors"
      data-testid={`faq-question-${question.substring(0, 20).replace(/\s/g, '-')}`}
    >
      <span className="font-medium text-slate-900 pr-4">{question}</span>
      {isOpen ? (
        <ChevronUp className="w-5 h-5 text-indigo-600 flex-shrink-0" />
      ) : (
        <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
      )}
    </button>
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="overflow-hidden"
        >
          <p className="px-4 pb-4 text-slate-600 leading-relaxed">{answer}</p>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const FAQCategory = ({ category, icon: Icon, color, questions }) => {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4 }}
      className="bg-white rounded-2xl shadow-lg overflow-hidden"
    >
      <div className={`bg-gradient-to-r ${color} p-4 flex items-center space-x-3`}>
        <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
          <Icon className="w-5 h-5 text-white" />
        </div>
        <h2 className="text-xl font-bold text-white">{category}</h2>
      </div>
      <div>
        {questions.map((faq, index) => (
          <FAQItem
            key={index}
            question={faq.q}
            answer={faq.a}
            isOpen={openIndex === index}
            onClick={() => setOpenIndex(openIndex === index ? null : index)}
          />
        ))}
      </div>
    </motion.div>
  );
};

export default function FAQPage() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Hero Section */}
      <div className="relative overflow-hidden" style={{ background: 'linear-gradient(to bottom right, #4F46E5, #7C3AED, #4F46E5)' }}>
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <HelpCircle className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Frequently Asked Questions
            </h1>
            <p className="text-lg text-indigo-100 max-w-2xl mx-auto">
              Everything you need to know about trading on Bid2Barter. Can't find what you're looking for? Contact our support team!
            </p>
          </motion.div>
        </div>
      </div>

      {/* FAQ Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="space-y-8">
          {faqs.map((category, index) => (
            <FAQCategory
              key={index}
              category={category.category}
              icon={category.icon}
              color={category.color}
              questions={category.questions}
            />
          ))}
        </div>

        {/* Contact Support Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
          className="mt-12 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-8 text-center"
        >
          <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Mail className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Still have questions?</h2>
          <p className="text-indigo-100 mb-6">
            Our support team is here to help you with any queries or issues.
          </p>
          <a
            href="mailto:bid2barter@gmail.com"
            className="inline-flex items-center space-x-2 bg-white text-indigo-600 font-bold px-6 py-3 rounded-full hover:bg-indigo-50 transition-colors shadow-lg"
            data-testid="support-email-link"
          >
            <Mail className="w-5 h-5" />
            <span>bid2barter@gmail.com</span>
          </a>
          <p className="text-indigo-200 text-sm mt-4">
            We typically respond within 24-48 hours
          </p>
        </motion.div>

        {/* Quick Links */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4">
          <a
            href="/create"
            className="flex items-center space-x-3 p-4 bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow border border-slate-200"
          >
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <Package className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="font-semibold text-slate-900">Create Listing</p>
              <p className="text-sm text-slate-500">Start trading now</p>
            </div>
          </a>
          <a
            href="/"
            className="flex items-center space-x-3 p-4 bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow border border-slate-200"
          >
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <RefreshCw className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="font-semibold text-slate-900">Browse Listings</p>
              <p className="text-sm text-slate-500">Find what you want</p>
            </div>
          </a>
          <a
            href="/auth"
            className="flex items-center space-x-3 p-4 bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow border border-slate-200"
          >
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="font-semibold text-slate-900">Join Bid2Barter</p>
              <p className="text-sm text-slate-500">Create an account</p>
            </div>
          </a>
        </div>
      </div>
    </div>
  );
}
